import { useRef, useState, useEffect, useCallback, memo } from "react"
import type { EmblaCarouselType } from "embla-carousel"
import classNames from "classnames"
import VideoPlayer from "~/components/VideoPlayer"
import ThumbnailCircle from "~/components/design-system/atoms/ThumbnailCircle"
import Carousel from "../Carousel"
import useHorizontalWheelScroll from "~/hooks/useHorizontalWheelScroll"

export interface GalleryItem {
  id: string
  type: "image" | "video"
  largeUrl: string
  smallUrl: string
  thumbnailSrc?: string
  alt?: string
  playbackUrl?: string
}

export interface MediaGalleryWithThumbnailsProps {
  items: GalleryItem[]
  selectedId: string
  onSelect: (id: string) => void
  className?: string
}

const MediaGalleryWithThumbnailsBase = ({
  items,
  selectedId,
  onSelect,
  className
}: MediaGalleryWithThumbnailsProps) => {
  const emblaRef = useRef<EmblaCarouselType | null>(null)
  const [thumbnailEmbla, setThumbnailEmbla] =
    useState<EmblaCarouselType | null>(null)

  const handleCarouselInit = useCallback(
    (embla: EmblaCarouselType) => {
      emblaRef.current = embla
      embla.on("select", () => {
        const index = embla.selectedScrollSnap()
        const item = items[index]
        if (item) onSelect(item.id)
      })
    },
    [items, onSelect]
  )

  // Enable horizontal wheel scrolling on the thumbnail carousel
  useHorizontalWheelScroll(thumbnailEmbla)

  useEffect(() => {
    const index = items.findIndex(item => item.id === selectedId)
    if (index !== -1 && emblaRef.current) {
      emblaRef.current.scrollTo(index)
    }
  }, [selectedId, items])

  useEffect(() => {
    const index = items.findIndex(item => item.id === selectedId)
    if (index !== -1 && thumbnailEmbla) {
      thumbnailEmbla.scrollTo(index)
    }
  }, [selectedId, items, thumbnailEmbla])

  const renderMediaContent = (item: GalleryItem) =>
    item.type === "video" ? (
      <VideoPlayer
        src={item.playbackUrl ?? item.largeUrl}
        poster={item.thumbnailSrc ?? item.largeUrl}
        minimalControls
        autoPlay
        loop
        active={item.id === selectedId}
        accessibilityLabel={item.alt}
      />
    ) : (
      <picture className="block h-full w-full overflow-hidden">
        <source
          srcSet={`${item.smallUrl} 500w, ${item.largeUrl} 1000w`}
          sizes="(max-width: 760px) 50w, 100vw"
        />
        <img
          className="h-full w-full object-cover"
          src={item.smallUrl as string}
          loading="lazy"
          alt={item.alt}
          draggable={false}
        />
      </picture>
    )

  const selectedItem = items.find(item => item.id === selectedId) ?? items[0]

  return (
    <div className={classNames("flex flex-col gap-4", className)}>
      {items.length > 1 ? (
        <Carousel
          className="rounded-xl bg-blue-100"
          slidesToShow={1}
          gap={0}
          arrows={false}
          onInit={handleCarouselInit}
        >
          {items.map(item => (
            <div key={item.id} className="aspect-[3/2]">
              {renderMediaContent(item)}
            </div>
          ))}
        </Carousel>
      ) : (
        <div className="w-full overflow-hidden rounded-xl bg-blue-100 aspect-[3/2]">
          {selectedItem && renderMediaContent(selectedItem)}
        </div>
      )}

      {items.length > 1 && (
        // -mx-4 cancels the page's mobile px-4 so thumbnails bleed off the
        // right edge, while pl-4 keeps the first thumbnail lined up with the
        // content above. Padding also gives the selected thumbnail's ring +
        // offset room inside the overflow-hidden viewport so it isn't clipped.
        <Carousel
          className="-mx-4 my-2 py-1.5 pl-4 pr-1.5 md:mx-0 md:pl-1.5"
          gap={6}
          arrows={false}
          paginationDots={true}
          onInit={setThumbnailEmbla}
        >
          {items.map(item => (
            // !w-auto overrides the carousel's computed slide width so slides
            // hug the thumbnail: gaps stay exactly `gap` px and the next
            // thumbnail peeks in from the edge.
            <div key={item.id} className="flex justify-center !w-auto">
              <ThumbnailCircle
                src={item.thumbnailSrc ?? item.smallUrl}
                alt={item.alt}
                isVideo={item.type === "video"}
                selected={item.id === selectedId}
                onClick={() => onSelect(item.id)}
              />
            </div>
          ))}
        </Carousel>
      )}
    </div>
  )
}

MediaGalleryWithThumbnailsBase.displayName = "MediaGalleryWithThumbnails"

/**
 * Memoized so unrelated re-renders of the Product Hero (e.g. the "from $X"
 * price loading into the CTA) don't re-render the gallery and re-initialize the
 * carousel, which caused a visible image flash. Only re-renders when items,
 * selectedId or onSelect actually change.
 */
const MediaGalleryWithThumbnails = memo(MediaGalleryWithThumbnailsBase)

export { MediaGalleryWithThumbnails }
export default MediaGalleryWithThumbnails
